import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Toolbar, Container, Grid, Paper, Button, Chip, Avatar, Divider, Typography } from '@mui/material';
import DialogNew from './DialogNew';
import AppBarAdmin from '../form/AppBarAdmin';
import DrawerAdmin from '../form/DrawerAdmin';
import PhotoBox from './PhotoBox';
import InfoBox from './InfoBox';
import StudentTable from './StudentTable';
import HomeworkTable from './HomeworkTable';
import DialogProfile from '../form/DialogProfile';
import DialogAssign from '../form/DialogAssign';
import DialogNewHomework from '../form/DialogNewHomework';
import { onSnapshot, collection, query, where, arrayRemove, doc, updateDoc, getDoc, documentId } from '@firebase/firestore';
import { db } from '../service/Firebase-config';
import { chunkArray, CopyRight } from '../common/util';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';
import DeleteConfirmation from '../common/DeleteConfirmation';

const defaultTheme = createTheme();

const DashboardAdmin = () => {
  const { selectedUser, setSelectedUser } = useGlobalContext();
  const { group } = useHomeworkContext();

  const [openAppBar, setOpenAppBar] = useState(true);
  const [openProfile, setOpenProfile] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [openDialogNew, setOpenDialogNew] = useState(false);
  const [teacherList, setTeacherList] = useState([]);
  const [openDeletion, setOpenDeletion] = useState(null);
  const [openNewHw, setOpenNewHw] = useState(false);

  // New Homework Dialog Handlers
  const openNewHomework = () => setOpenNewHw(true);
  const closeNewHomework = () => setOpenNewHw(false);

  const toggleDrawer = () => {
    setOpenAppBar(!openAppBar);
  };

  const handleClickOpen = () => {
    setOpenDialogNew(true);
  };

  const handleCancel = () => {
    setOpenDialogNew(false);
  };

  const handleDeleteTeacher = async (tid) => {
    const selectedRowRef = doc(db, 'teachers', tid);
    await updateDoc(selectedRowRef, { studentList: arrayRemove(selectedUser.uid) });

    const selectedStudentRef = doc(db, 'students', selectedUser.uid);
    await updateDoc(selectedStudentRef, { teacherList: selectedUser.teacherList.filter(t => t !== tid) }).then(async () => {
      const updatedInfo = await getDoc(doc(db, 'students', selectedUser.uid));
      setSelectedUser(updatedInfo.data());
    });
    setOpenDeletion(null);
  };

  useEffect(() => {
    if (group === "students" && selectedUser?.teacherList?.length > 0) {
      const teacherCollectionRef = collection(db, "teachers");

      const chunks = chunkArray(selectedUser.teacherList, 30);
      let allTeachers = new Map();
      let unsubscribes = [];
  
      chunks.forEach((chunk) => {
        const whereQuery = query(teacherCollectionRef, where(documentId(), "in", chunk));
  
        const unsubscribe = onSnapshot(whereQuery, (snapshot) => {
          const teacherList = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
  
          teacherList.forEach((teacher) => allTeachers.set(teacher.id, teacher));
  
          const sortedTeachers = Array.from(allTeachers.values()).sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );
  
          setTeacherList(sortedTeachers);
        });
  
        unsubscribes.push(unsubscribe);
      });
  
      return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
    } else {
      setTeacherList([]);
    }
  }, [group, selectedUser]);
  
  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBarAdmin open={openAppBar} toggleDrawer={toggleDrawer} />
        <DrawerAdmin open={openAppBar} toggleDrawer={toggleDrawer} handleClickOpen={handleClickOpen} />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Grid container spacing={3}>
              {group === 'students' ? (
                <>
                  <Grid item xs={12} md={6} lg={6}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 300,
                      }}
                    >
                      <InfoBox expectedRole={'admin'} />
                      <Box sx={{ pt: 2, display: 'flex', gap: 2 }}>
                        {selectedUser && (
                          <Button variant="contained" onClick={() => setOpenProfile(true)}>
                            Edit Profile
                          </Button>
                        )}
                        {selectedUser && (
                          <Button variant="contained" onClick={openNewHomework}>
                            Create Homework
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={2.5} lg={2.5}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 300,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>Assigned Teachers</Typography>
                      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {teacherList && teacherList
                            .filter((item) => item.active)
                            .map((item) => (
                              <Grid item key={item.uid}>
                                <Chip
                                  label={item.displayName}
                                  variant="outlined"
                                  color="primary"
                                  avatar={<Avatar alt={item.displayName} src={item.photoUrl} />}
                                  onDelete={() => setOpenDeletion(item)}
                                />
                              </Grid>
                            ))}
                        </Grid>
                      </Box>
                      <Divider sx={{ mt: 2 }} />
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        {selectedUser && <Button variant="contained" disabled={!selectedUser?.active} onClick={() => setOpenAssign(true)}>Assign Teachers</Button>}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={3.5} lg={3.5}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 300,
                      }}
                    >
                      <PhotoBox selectedUser={selectedUser}/>
                    </Paper>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={8} lg={9}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 240,
                        overflow: 'hidden'
                      }}
                    >
                      <Grid container spacing={4}>
                        <Grid item sx={{ minWidth: '600px' }}>
                          <InfoBox expectedRole={'admin'} />
                          <Box sx={{ pt: 2 }}>
                            {selectedUser && <Button variant="contained" onClick={() => setOpenProfile(true)}>Edit Profile</Button>}
                          </Box>
                        </Grid>
                        {group === 'students' && (
                          <>
                            <Grid item>
                              <Divider orientation="vertical" variant="middle" />
                            </Grid>
                            <Grid item>
                              <Grid container direction="column" spacing={1}>
                                {teacherList && teacherList
                                  .filter((item) => item.active)
                                  .map((item) => (
                                    <Grid item key={item.uid}>
                                      <Chip
                                        label={item.displayName}
                                        variant="outlined"
                                        color="primary"
                                        avatar={<Avatar alt={item.displayName} src={item.photoUrl} />}
                                        onDelete={() => setOpenDeletion(item)}
                                      />
                                    </Grid>
                                  ))}
                              </Grid>
                              <Grid container display="flex" alignItems="end" sx={{ minHeight: 50 }}>
                                <Box display="flex" alignItems="end">
                                  {selectedUser && <Button variant="contained" disabled={!selectedUser?.active} onClick={() => setOpenAssign(true)}>Assign Teachers</Button>}
                                </Box>
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4} lg={3}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 240,
                      }}
                    >
                      <PhotoBox selectedUser={selectedUser}/>
                    </Paper>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                  {group === 'students' ? (
                    <HomeworkTable />
                  ) : (
                    <StudentTable />
                  )}
                </Paper>
              </Grid>
            </Grid>
            {CopyRight()}
          </Container>
        </Box>
      </Box>
      <DialogNew open={openDialogNew} handleCancel={handleCancel} isStudent={group === "students"} />
      <DialogAssign open={openAssign} handleClose={() => setOpenAssign(false)} />
      <DialogProfile open={openProfile} handleClose={() => setOpenProfile(false)} selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
      <DialogNewHomework open={openNewHw} closeNewHomework={closeNewHomework} isAdmin={true} />
      <DeleteConfirmation
        open={Boolean(openDeletion)}
        closeDelete={() => setOpenDeletion(null)}
        handleDelete={() => handleDeleteTeacher(openDeletion?.uid)}
        title={`Are you sure you want to unassign ${openDeletion?.displayName}?`}
        deleteBtn='Unassign'
      />
    </ThemeProvider>
  );
}

export default DashboardAdmin;
